// src/pages/AgentCommandCenter/AgentCommandCenter.tsx
import React, { useState } from 'react';
import styles from './AgentCommandCenter.module.scss';

// Import types
import { DateFilterValue, CallInfo } from 'types/agentCommandCenter.type';

// Import components
import Header from 'components/AgentCommandCenter/Header';
import CallConsole from 'components/AgentCommandCenter/CallConsole';
import ExpandedConsoleModal from 'components/AgentCommandCenter/ExpandedConsoleModal';
import OutcomeModal from 'components/AgentCommandCenter/OutcomeModal';
import { AudioModal, TranscriptModal } from 'components';
import { useFetchAgentCenterData } from 'components/AgentCallConsole/hooks/useFetchAgentCenterData';
import { useAgentCallConsole } from 'components/AgentCallConsole/hooks/useAgentCallConsole';

import { CallbackMetrics, CallHistory, ManualCallbacks } from './components';
import { formatCallHistory } from './AgentCommandCenter.utils';
import { SyncTranscript } from 'components/SyncTranscript';

const AgentCommandCenter = () => {
    const {
        room,
        callStatus,
        callTranscript,
        qualifications,
        availableStatuses,
        callDetails,
        clientInfo,
        agentAvailable,
        isMuted,
        isOnHold,

        handleToggle,
        handleMuteToggle,
        handleHoldToggle,
        handleAcceptCall,
        handleRejectCall,
        handleHangupCall,
        handleMakeCall,
    } = useAgentCallConsole();

    const [showExpandedConsole, setShowExpandedConsole] = useState(false);
    const [showOutcomeModal, setShowOutcomeModal] = useState(false);
    const [showTranscriptModal, setShowTranscriptModal] = useState(false);
    const [audioModalOpen, setAudioModalOpen] = useState(false);
    const [isSyncTranscriptOpen, setIsSyncTranscriptOpen] = useState(false);

    const [currentCallId, setCurrentCallId] = useState<string | null>(null);
    const [currentCallInfo, setCurrentCallInfo] = useState<CallInfo | undefined>(undefined);

    // Add date filter state
    const [dateFilter, setDateFilter] = useState<DateFilterValue>('today');

    // Handler for transcript click
    const handleViewTranscript = (callId: string, callInfo: CallInfo) => {
        setCurrentCallId(callId);
        // Map FormattedCallHistory to CallInfo structure that TranscriptModal expects
        setCurrentCallInfo({
            name: callInfo.name,
            callDate: callInfo.callDate,
            callTime: callInfo.callTime,
        });
        setShowTranscriptModal(true);
    };

    // Close transcript modal
    const closeTranscriptModal = (): void => {
        setShowTranscriptModal(false);
        setCurrentCallId(null);
        setCurrentCallInfo(undefined);
    };

    // Handler for date filter change
    const handleDateFilterChange = (value: DateFilterValue): void => {
        console.log('Date filter changed:', value);
        setDateFilter(value);
    };

    // Fetch agent center data on component mount
    const { agentCenterData, loading, error } = useFetchAgentCenterData();

    // Add this handler function near handleViewTranscript
    const handleRecordingClick = (callId: string, callInfo: CallInfo) => {
        setCurrentCallId(callId);
        setCurrentCallInfo(callInfo);
        setAudioModalOpen(true);
    };

    const handleViewSyncTranscript = (callId: string, callInfo: CallInfo) => {
        setCurrentCallId(callId);
        setCurrentCallInfo(callInfo);
        setIsSyncTranscriptOpen(true);
    };

    // UI states for expanded console and outcome modal
    const openExpandedConsole = (): void => setShowExpandedConsole(true);
    const closeExpandedConsole = (): void => setShowExpandedConsole(false);
    const openOutcomeModal = (): void => setShowOutcomeModal(true);
    const closeOutcomeModal = (): void => setShowOutcomeModal(false);

    if (loading) {
        return (
            <div className={styles.agentCommandCenterContainer}>
                <Header
                    isActive={agentAvailable}
                    onToggle={handleToggle}
                    callStatus={callStatus}
                    dateFilter={dateFilter}
                    onDateFilterChange={handleDateFilterChange}
                />
                <div className={styles.loadingContainer}>
                    <p>Loading agent data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.agentCommandCenterContainer}>
                <Header
                    isActive={agentAvailable}
                    onToggle={handleToggle}
                    callStatus={callStatus}
                    dateFilter={dateFilter}
                    onDateFilterChange={handleDateFilterChange}
                />
                <div className={styles.errorContainer}>
                    <p>Error loading agent data. Please try again later.</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.agentCommandCenterContainer}>
            {/* HEADER */}
            <Header
                isActive={agentAvailable}
                onToggle={handleToggle}
                callStatus={callStatus}
                dateFilter={dateFilter}
                onDateFilterChange={handleDateFilterChange}
            />

            {/* MAIN CONTENT */}
            <main className={`${styles.mainContent} main-container h-full`}>
                <div className={styles.mainContentContainer}>
                    <CallbackMetrics />

                    {/* LEFT MAIN SECTION */}
                    <section className={styles.leftMainSection}>
                        <ManualCallbacks handleViewSyncTranscript={handleViewSyncTranscript} />

                        <CallHistory
                            calls={formatCallHistory({ agentCenterData, dateFilter })}
                            onViewTranscript={handleViewTranscript}
                            onListenRecording={handleRecordingClick}
                            isLoading={loading}
                        />
                    </section>
                </div>

                {/* RIGHT PANEL */}
                <aside className={styles.rightPanel}>
                    <CallConsole
                        client={clientInfo}
                        qualifications={qualifications}
                        transcriptMessages={callTranscript}
                        onExpandConsole={openExpandedConsole}
                        callStatus={callStatus}
                        onAcceptCall={handleAcceptCall}
                        onRejectCall={handleRejectCall}
                        onHangupCall={handleHangupCall}
                        onMakeCall={handleMakeCall}
                        isMuted={isMuted}
                        onMuteToggle={handleMuteToggle}
                        isOnHold={isOnHold}
                        onHoldToggle={handleHoldToggle}
                        isActive={agentAvailable}
                        statuses={availableStatuses}
                        callDetails={callDetails}
                    />
                </aside>
            </main>

            {/* The Expand Modal */}
            <ExpandedConsoleModal
                isOpen={showExpandedConsole}
                onClose={closeExpandedConsole}
                client={clientInfo}
                qualifications={qualifications}
                transcriptMessages={callTranscript}
                onOpenOutcomeModal={openOutcomeModal}
                callStatus={callStatus}
                onAcceptCall={handleAcceptCall}
                onRejectCall={handleRejectCall}
                onHangupCall={handleHangupCall}
                isActive={agentAvailable}
            />

            {/* The "Call Outcome" modal */}
            <OutcomeModal isOpen={showOutcomeModal} onClose={closeOutcomeModal} />

            {/* Transcript Modal */}
            <TranscriptModal
                isOpen={showTranscriptModal}
                onClose={closeTranscriptModal}
                callId={currentCallId}
                callInfo={currentCallInfo}
            />

            {/* Audio Modal */}
            <AudioModal
                isOpen={audioModalOpen}
                onClose={() => {
                    setAudioModalOpen(false);
                    setCurrentCallId(null);
                    setCurrentCallInfo(undefined);
                }}
                callId={currentCallId}
                callInfo={currentCallInfo}
            />

            {currentCallId && (
                <SyncTranscript
                    callId={currentCallId}
                    isOpen={isSyncTranscriptOpen}
                    onClose={() => setIsSyncTranscriptOpen(false)}
                    callInfo={currentCallInfo}
                />
            )}
        </div>
    );
};

export default AgentCommandCenter;
